import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, renderList as _renderList, createBlock as _createBlock, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { style: {"font-size":"medium"} }
const _hoisted_2 = { for: "compute-name" }
const _hoisted_3 = { for: "bucket-description" }
const _hoisted_4 = { class: "mt-4 mb-1" }
const _hoisted_5 = { class: "mt-3" }
const _hoisted_6 = { class: "mt-4 mb-1" }
const _hoisted_7 = { class: "text-muted mb-1" }
const _hoisted_8 = { class: "row" }
const _hoisted_9 = { class: "mt-3" }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = { key: 2 }

import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { v4 as uuidv4 } from 'uuid';
import {
  ComputeTaskCreateDocument,
  CouponApplicationInput,
  SampleType,
} from '@/gql/graphql';
import {
  BAlert,
  BBadge,
  BButton,
  BCollapse,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BFormTextarea,
  BModal,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';
import {
  COUPON_TYPE_NAMES,
  COUPON_UNITS,
  COMPUTE_TASK_TYPES,
} from '@/constants';
import CodeValidation, { DefaultCode } from '@/components/CodeValidation.vue';
import { useUserStore } from '@/store/user';
import { Attributes_NextFlowPipeline } from '@/utils/compute-utils';
import CloudRegionSelection from '@/components/compute/CloudRegionSelection.vue';
import DeveloperOptions from '@/components/compute/DeveloperOptions.vue';
import PipelinePicker from '@/components/compute/PipelinePicker.vue';
import { PipelineType } from '@/gql/graphql';


export default /*@__PURE__*/_defineComponent({
  __name: 'SampleReconstructionNextFlow',
  props: {
    show: { type: Boolean },
    projectId: {},
    projectName: {},
    unitsRequired: {},
    defaultCodes: {},
    defaultName: {},
    samples: {}
  },
  emits: ['close', 'closeAndRefresh'],
  setup(__props: any, { emit: __emit }) {

const userStore = useUserStore();

// --- Component references ---
const codeValidation = ref<InstanceType<typeof CodeValidation>>();
const devOptions = ref<InstanceType<typeof DeveloperOptions>>();

// --- Component properties ---
const props = __props;

const emits = __emit;

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});

const computeName = ref<string>(props.defaultName);
const computeDescription = ref<string>('');
const computeUnitsRequired = computed(() => {
  return selectedSampleIndices.value.length;
});

const customCloudRegionPrefix = ref<string | null>(null);
const customCloudRegionRequired = ref(false);

const advancedOptionsShow = ref<boolean>(false);

const adminPipelineBarcodeWhitelist = ref<string>('');
const adminPipelineReferenceOrganism = ref<string>('');
const adminPipelineUmiLength = ref<number>(8);
const adminPipelineUmiLengthOverride = ref<boolean>(false);

// --- NextFlow Pipeline specific state ---
const selectedPipeline = ref<PipelineType | null>(null);
const nfCustomWorkflow = ref<boolean>(false);
const nfWorkflowLocation = ref<string>('');

// --- Sample Selection (select all samples by default) ---
const selectedSampleIndices = ref<number[]>(props.samples.map((_, i) => i));

function selectAllSamples() {
  selectedSampleIndices.value = props.samples.map((_, i) => i);
}
function clearAllSamples() {
  selectedSampleIndices.value = [];
}

const selectedSamplesCount = computed(() => selectedSampleIndices.value.length);

// --- ComputeTaskCreate Mutation ---
const mutationError = ref<string | null>(null);
const mutationInProgress = ref<boolean>(false);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(ComputeTaskCreateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.computeTaskCreate) {
    console.debug('Sample reconstruction started:', res.data.computeTaskCreate);
    mutationShowSuccess.value = true;
    setTimeout(() => {
      emits('closeAndRefresh');
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    mutationError.value = 'Sample reconstruction start failed: ' + res.data;
    console.error('Sample reconstruction start failed:', res.data);
  }
});

onMutationError((err) => {
  console.error('Error starting sample reconstruction:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function mutationRequested() {
  mutationInProgress.value = true;
  mutationError.value = null;
  mutationShowSuccess.value = false;

  if (devOptions.value?.extraAttributesInvalid) {
    mutationError.value =
      'Developer JSON attributes must be a valid JSON object.';
    mutationInProgress.value = false;
    return;
  }

  // validate code requirements
  if (!codeValidation.value || codeValidation.value.failure) {
    console.error('Invalid kit or coupon code', codeValidation.value);
    mutationError.value = 'Please provide a valid kit or coupon code.';
    mutationInProgress.value = false;
    return;
  }

  // initialiaze coupon applications
  const couponApplications: CouponApplicationInput[] =
    codeValidation?.value?.codeValidationItems.map((item) => ({
      code: item.codeString,
      unitsDeclared: item.unitsDeclared,
    })) || [];

  // initialize attributes
  let attributes: Attributes_NextFlowPipeline = {
    gceRequestId: uuidv4(),
    samples: [],
  };

  // make sure at least one sample is selected
  if (selectedSampleIndices.value.length === 0) {
    mutationError.value = 'Please select at least one sample.';
    mutationInProgress.value = false;
    return;
  }

  // select samples and populate attributes
  let selectedSamples = selectedSampleIndices.value.map(
    (i) => props.samples[i]
  );

  // populate samples
  selectedSamples.forEach((sample: SampleType) => {
    // validate sample
    if (!sample.name) {
      console.error('Sample name not provided:', sample);
      mutationError.value = 'Please provide a name for each sample.';
      mutationInProgress.value = false;
      return;
    }
    if (!sample.fileR1 || !sample.fileR2) {
      console.error('Sample files not provided:', sample);
      (mutationError.value =
        'Please provide both R1 and R2 files for each sample. Sample '),
        sample.name,
        ' is missing files.';
      mutationInProgress.value = false;
      return;
    }
    if (!sample.bucket || !sample.bucket.name) {
      console.error('Sample bucket not provided:', sample);
      (mutationError.value =
        'Please provide a bucket for each sample. Sample '),
        sample.name,
        ' is missing a bucket.';
      mutationInProgress.value = false;
      return;
    }
    if (!sample.kit?.barcode) {
      console.error('Barcode not provided:', sample);
      (mutationError.value =
        'Please provide a barcode for each sample. Sample '),
        sample.name,
        ' is missing a barcode.';
      mutationInProgress.value = false;
      return;
    }
    if (!sample.kit?.umiLength) {
      console.error('UMI length not provided:', sample);
      (mutationError.value =
        'Please specify UMI length for each sample. Sample '),
        sample.name,
        ' is missing the UMI length specification.';
      mutationInProgress.value = false;
      return;
    }
    // push sample to collection
    attributes.samples.push({
      name: sample.name,
      fileR1: sample.fileR1,
      fileR2: sample.fileR2,
      bucketInputName: sample.bucket?.name,
      barcodesWhitelist: sample.kit?.barcode,
      umiLength: sample.kit?.umiLength,
      reference: 'jurkat',
      referenceTranscriptomeProfile: 'jurkat',
    });
  });

  // ADMIN OPTIONS
  if (userStore.admin && advancedOptionsShow.value) {
    // barcode whitelist override
    if (adminPipelineBarcodeWhitelist.value) {
      attributes.samples.forEach((sample) => {
        sample.barcodesWhitelist = adminPipelineBarcodeWhitelist.value;
      });
    }

    // reference organism override
    if (adminPipelineReferenceOrganism.value) {
      attributes.samples.forEach((sample) => {
        // see if comma-separated values were provided
        // - split the string by comma
        // - trim all items by white-space and make it an array
        // - filter out empty items
        const referenceOrganismItems = adminPipelineReferenceOrganism.value
          .split(',')
          .map((item) => item.trim())
          .filter((item) => item !== '');

        // if more than one item, use the array
        if (referenceOrganismItems.length > 0) {
          sample.referenceOrganism = referenceOrganismItems;
        }
      });
    }

    // umi length override
    if (adminPipelineUmiLengthOverride.value) {
      attributes.samples.forEach((sample) => {
        sample.umiLength = adminPipelineUmiLength.value;
      });
    }
  }

  // CATALOG PIPELINE
  if (nfCustomWorkflow.value) {
    attributes.workflowLocation = nfWorkflowLocation.value;
  } else if (selectedPipeline.value && selectedPipeline.value.id) {
    attributes.pipelineId = selectedPipeline.value.id;
  }

  // DEVELOPER OPTIONS
  if (userStore.developer && devOptions.value?.expanded) {
    // general - version
    if (devOptions.value.version) {
      attributes.version = devOptions.value.version;
    }

    // general - vm lifecycle
    if (devOptions.value.vmLifecycle) {
      attributes.vmLifecycle = devOptions.value.vmLifecycle;
    }

    // general - machine type
    if (devOptions.value.machineType) {
      attributes.machineType = devOptions.value.machineType;
    }

    // data pipeline - min pipeline
    if (devOptions.value.dataPipelineMin) {
      attributes.minPipelineRequested = true;
    }

    // data pipeline - copy data
    if (devOptions.value.dataPipelineCopyData) {
      attributes.needsDataCopying = true;
    }

    // extra attributes from JSON
    if (devOptions.value.extraAttributes) {
      Object.assign(attributes, devOptions.value.extraAttributes);
    }
  }

  console.debug(
    'New sample reconstruction requested (NextFlow), task attributes:',
    attributes
  );

  // mutationInProgress.value = false;
  executeMutation({
    name: computeName.value,
    description: computeDescription.value,
    coupons: couponApplications,
    projectId: props.projectId,
    taskType: COMPUTE_TASK_TYPES.NEXTFLOW_PIPELINE,
    attributes: attributes,
    requestedRegionPrefix: customCloudRegionPrefix.value
      ? customCloudRegionPrefix.value
      : undefined,
  });
}

function onModalHide(evt: Event) {
  // prevent closing modal if mutation is in progress
  if (mutationInProgress.value) {
    evt.preventDefault();
    return;
  }
  // otherwise
  emits('close');
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BModal), {
      modelValue: show.value,
      "onUpdate:modelValue": _cache[19] || (_cache[19] = ($event: any) => ((show).value = $event)),
      backdrop: mutationInProgress.value ? 'static' : true,
      "no-close-on-esc": mutationInProgress.value,
      onHide: onModalHide,
      class: "modal-lg"
    }, {
      title: _withCtx(() => [
        _createVNode(_component_font_awesome_icon, {
          icon: "wand-magic-sparkles",
          class: "me-2"
        }),
        _createElementVNode("strong", null, _toDisplayString(props.projectName), 1),
        _cache[21] || (_cache[21] = _createElementVNode("br", null, null, -1)),
        _createElementVNode("span", _hoisted_1, [
          _createVNode(_unref(BBadge), { variant: "warning" }, {
            default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
              _createElementVNode("i", { class: "fab fa-neos" }, null, -1),
              _createTextVNode(" NextFlow ", -1)
            ]))]),
            _: 1
          })
        ])
      ]),
      footer: _withCtx(() => [
        _createVNode(_unref(BButton), {
          variant: "secondary",
          onClick: onModalHide,
          disabled: mutationInProgress.value
        }, {
          default: _withCtx(() => [...(_cache[46] || (_cache[46] = [
            _createTextVNode(" Cancel ", -1)
          ]))]),
          _: 1
        }, 8, ["disabled"]),
        _createVNode(_unref(BButton), {
          variant: "primary",
          onClick: mutationRequested,
          disabled: 
            codeValidation.value?.failure || mutationInProgress.value || mutationShowSuccess.value
          
        }, {
          default: _withCtx(() => [
            (mutationInProgress.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_10, [
                  _cache[47] || (_cache[47] = _createTextVNode(" Starting Reconstruction ", -1)),
                  _createVNode(_unref(BSpinner), {
                    small: "",
                    label: ""
                  })
                ]))
              : (mutationShowSuccess.value)
                ? (_openBlock(), _createElementBlock("span", _hoisted_11, [
                    _cache[48] || (_cache[48] = _createTextVNode(" Reconstruction Started ", -1)),
                    _createVNode(_component_font_awesome_icon, {
                      icon: ['fa', 'check-double'],
                      class: "me-2"
                    })
                  ]))
                : (_openBlock(), _createElementBlock("span", _hoisted_12, " Start Reconstruction "))
          ]),
          _: 1
        }, 8, ["disabled"])
      ]),
      default: _withCtx(() => [
        _createElementVNode("div", null, [
          _cache[36] || (_cache[36] = _createElementVNode("h5", null, "Run identification", -1)),
          _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
            default: _withCtx(() => [
              _createElementVNode("label", _hoisted_2, [
                _createVNode(_component_font_awesome_icon, {
                  icon: ['fas', 'tag'],
                  class: "me-1"
                }),
                _cache[23] || (_cache[23] = _createTextVNode(" Reconstruction label ", -1)),
                _createVNode(_component_font_awesome_icon, {
                  icon: ['fas', 'question-circle'],
                  class: "ms-2 text-secondary",
                  id: "compute-name-help-icon"
                }),
                _createVNode(_unref(BTooltip), { target: "compute-name-help-icon" }, {
                  default: _withCtx(() => [...(_cache[22] || (_cache[22] = [
                    _createTextVNode("Label to help you identify the reconstruction run later. ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _createVNode(_unref(BFormInput), {
                modelValue: computeName.value,
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((computeName).value = $event)),
                id: "compute-name",
                name: "compute-name",
                placeholder: "Enter reconstruction label",
                required: ""
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
            default: _withCtx(() => [
              _createElementVNode("label", _hoisted_3, [
                _createVNode(_component_font_awesome_icon, {
                  icon: ['fas', 'align-justify'],
                  class: "me-1"
                }),
                _cache[25] || (_cache[25] = _createTextVNode(" Description ", -1)),
                _createVNode(_component_font_awesome_icon, {
                  icon: ['fas', 'question-circle'],
                  class: "ms-2 text-secondary",
                  id: "compute-description-help-icon"
                }),
                _createVNode(_unref(BTooltip), { target: "compute-description-help-icon" }, {
                  default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                    _createTextVNode(" Optional description of the run for later reference. ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _createVNode(_unref(BFormTextarea), {
                id: "compute-description",
                name: "computeDescription",
                modelValue: computeDescription.value,
                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((computeDescription).value = $event)),
                type: "text",
                rows: "2",
                "max-rows": "10"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }),
          _createElementVNode("h5", _hoisted_4, [
            _createVNode(_component_font_awesome_icon, {
              icon: ['fas', 'server'],
              class: "me-2"
            }),
            _cache[26] || (_cache[26] = _createTextVNode(" Pipeline selection ", -1))
          ]),
          _createVNode(PipelinePicker, {
            class: "mt-3",
            modelValue: selectedPipeline.value,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((selectedPipeline).value = $event)),
            enabled: !nfCustomWorkflow.value,
            label: null,
            type: null,
            premium: null,
            activeOnly: true,
            allowNullPipeline: false
          }, null, 8, ["modelValue", "enabled"]),
          (_unref(userStore).developer)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createElementVNode("label", _hoisted_5, [
                  _cache[28] || (_cache[28] = _createTextVNode(" Custom Workflow (developer option) ", -1)),
                  _createVNode(_component_font_awesome_icon, {
                    icon: ['fas', 'question-circle'],
                    id: "nf-workflow-location-help",
                    class: "ms-2 text-secondary"
                  }),
                  _createVNode(_unref(BTooltip), {
                    target: "nf-workflow-location-help",
                    class: "large-tooltip"
                  }, {
                    default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                      _createElementVNode("div", { style: {"text-align":"justify"} }, [
                        _createElementVNode("p", null, "Specify the NextFlow workflow URL. Accepted options are:"),
                        _createElementVNode("ul", null, [
                          _createElementVNode("li", null, [
                            _createElementVNode("code", null, " https://gitlab.samplinghuman.com/group/project/-/tree/ref/workflow-path ")
                          ]),
                          _createElementVNode("li", null, [
                            _createElementVNode("code", null, " https://gitlab.samplinghuman.com/group/project/-/blob/ref/workflow-path/main.nf ")
                          ]),
                          _createElementVNode("li", null, [
                            _createElementVNode("code", null, " gitlab://group/project/-/raw/ref/workflow-path/main.nf ")
                          ]),
                          _createElementVNode("li", null, [
                            _createElementVNode("code", null, "gs://bucket/workflow-path/main.nf")
                          ])
                        ]),
                        _createElementVNode("p", null, [
                          _createTextVNode(" Best default for humans is to use the GitLab browser URL from the repository page. The backend canonicalizes that to the raw workflow source internally. Example browser URL: "),
                          _createElementVNode("code", null, " https://gitlab.samplinghuman.com/samplinghuman/ax-stack/nextflow/pipelines/rnaseq-data-pipeline/-/tree/v0.1.0-dev "),
                          _createTextVNode(" is automatically converted to canonical form: "),
                          _createElementVNode("code", null, " gitlab://samplinghuman/ax-stack/nextflow/pipelines/rnaseq-data-pipeline/-/raw/v0.1.0-dev/main.nf "),
                          _createTextVNode(" by the backend. ")
                        ])
                      ], -1)
                    ]))]),
                    _: 1
                  })
                ]),
                _createVNode(_unref(BFormGroup), null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(BInputGroup), null, {
                      prepend: _withCtx(() => [
                        _createVNode(_unref(BButton), { variant: "outline-secondary" }, {
                          default: _withCtx(() => [
                            _createVNode(_unref(BFormCheckbox), {
                              modelValue: nfCustomWorkflow.value,
                              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((nfCustomWorkflow).value = $event)),
                              name: "nf-custom-workflow"
                            }, null, 8, ["modelValue"])
                          ]),
                          _: 1
                        })
                      ]),
                      default: _withCtx(() => [
                        _createVNode(_unref(BFormInput), {
                          modelValue: nfWorkflowLocation.value,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((nfWorkflowLocation).value = $event)),
                          id: "nf-workflow-location",
                          name: "nf-workflow-location",
                          placeholder: "Enter workflow location",
                          disabled: !nfCustomWorkflow.value,
                          onChange: _cache[5] || (_cache[5] = ($event: any) => (nfCustomWorkflow.value = true))
                        }, null, 8, ["modelValue", "disabled"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ], 64))
            : _createCommentVNode("", true),
          _createElementVNode("h5", _hoisted_6, [
            _createVNode(_component_font_awesome_icon, {
              icon: ['fas', 'list-check'],
              class: "me-2"
            }),
            _cache[29] || (_cache[29] = _createTextVNode(" Samples to process ", -1))
          ]),
          _createElementVNode("small", _hoisted_7, [
            _createTextVNode(_toDisplayString(selectedSamplesCount.value) + " of " + _toDisplayString(props.samples.length) + " " + _toDisplayString(props.samples.length === 1 ? 'sample' : 'samples') + " selected (", 1),
            _createElementVNode("a", {
              href: "#",
              class: "text-decoration-none text-secondary",
              onClick: _withModifiers(selectAllSamples, ["prevent"])
            }, [
              _cache[30] || (_cache[30] = _createTextVNode(" select all ", -1)),
              _createVNode(_component_font_awesome_icon, {
                icon: ['fas', 'check-double'],
                class: "ms-1"
              })
            ]),
            _cache[32] || (_cache[32] = _createTextVNode(", ", -1)),
            _createElementVNode("a", {
              href: "#",
              class: "text-decoration-none text-secondary",
              onClick: _withModifiers(clearAllSamples, ["prevent"])
            }, [
              _cache[31] || (_cache[31] = _createTextVNode(" clear all ", -1)),
              _createVNode(_component_font_awesome_icon, {
                icon: ['fas', 'xmark'],
                class: "ms-1 me-1"
              })
            ]),
            _cache[33] || (_cache[33] = _createTextVNode(") ", -1))
          ]),
          _createVNode(_unref(BFormGroup), null, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_8, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.samples, (sample, index) => {
                  return (_openBlock(), _createElementBlock("div", {
                    class: "col-12 col-md-6",
                    key: sample.id ?? sample.name ?? index
                  }, [
                    _createVNode(_unref(BFormCheckbox), {
                      modelValue: selectedSampleIndices.value,
                      "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((selectedSampleIndices).value = $event)),
                      value: index,
                      name: "pipeline-sample-checkbox",
                      switch: "",
                      class: "my-1"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(sample.name || `Sample ${index + 1}`), 1)
                      ]),
                      _: 2
                    }, 1032, ["modelValue", "value"])
                  ]))
                }), 128))
              ])
            ]),
            _: 1
          }),
          _cache[37] || (_cache[37] = _createElementVNode("h5", { class: "mt-4" }, "Kit validation", -1)),
          _cache[38] || (_cache[38] = _createElementVNode("p", null, [
            _createElementVNode("small", null, " Kits associate your samples with the correct barcodes and make sure the subsequent processing is accurate. Kits also contain storage credits that allow you to upload data to the Sampling Human analytics cloud in the first place. Please do not exchange the correct kit or coupon codes if you run out of credits and contact support for assistance instead. This will ensure valid code association and prevent any disruptions in your workflow or data processing. ")
          ], -1)),
          _createVNode(CodeValidation, {
            class: "mt-3",
            ref_key: "codeValidation",
            ref: codeValidation,
            "label-text": "Kit\n        or coupon code",
            "button-text": "Apply",
            defaultCodes: props.defaultCodes,
            requireKit: false,
            requireExplicitCounts: false,
            requiredCouponTypeName: _unref(COUPON_TYPE_NAMES).COMPUTE,
            requriedCouponUnitsName: _unref(COUPON_UNITS).SAMPLES,
            requiredCouponUnitsCount: computeUnitsRequired.value,
            requiredLicenseTypeName: null,
            immediateValidation: true,
            hideComponentMessage: false
          }, null, 8, ["defaultCodes", "requiredCouponTypeName", "requriedCouponUnitsName", "requiredCouponUnitsCount"]),
          (codeValidation.value)
            ? (_openBlock(), _createBlock(_unref(BAlert), {
                key: 1,
                modelValue: codeValidation.value.success,
                "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((codeValidation.value.success) = $event)),
                variant: "success",
                class: "mt-3"
              }, {
                default: _withCtx(() => [
                  _cache[34] || (_cache[34] = _createTextVNode(" Please double-check the kit/coupon code before proceeding. Beware that ", -1)),
                  _createElementVNode("strong", null, _toDisplayString(computeUnitsRequired.value) + " compute credits", 1),
                  _cache[35] || (_cache[35] = _createTextVNode(" for sample reconstruction will be deducted from the provided kit/coupon. ", -1))
                ]),
                _: 1
              }, 8, ["modelValue"]))
            : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", {
          class: _normalizeClass([
          'mt-4',
          advancedOptionsShow.value ? 'expanded-group' : 'collapsed-group',
        ])
        }, [
          _createVNode(_unref(BButton), {
            variant: "outline-dark",
            class: "w-100",
            size: "sm",
            onClick: _cache[8] || (_cache[8] = ($event: any) => (advancedOptionsShow.value = !advancedOptionsShow.value))
          }, {
            default: _withCtx(() => [
              _createVNode(_component_font_awesome_icon, {
                icon: ['fas', 'cogs'],
                class: "me-2"
              }),
              _cache[39] || (_cache[39] = _createTextVNode(" Advanced Options ", -1)),
              _createVNode(_component_font_awesome_icon, {
                icon: ['fas', advancedOptionsShow.value ? 'angle-up' : 'angle-down'],
                class: "ms-2"
              }, null, 8, ["icon"])
            ]),
            _: 1
          }),
          _createVNode(_unref(BCollapse), {
            modelValue: advancedOptionsShow.value,
            "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((advancedOptionsShow).value = $event))
          }, {
            default: _withCtx(() => [
              _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
                default: _withCtx(() => [
                  _createElementVNode("label", null, [
                    _cache[41] || (_cache[41] = _createTextVNode(" Reference organism ", -1)),
                    _createVNode(_component_font_awesome_icon, {
                      id: "reference-organism-icon",
                      icon: "question-circle",
                      class: "ms-2 text-secondary"
                    }),
                    _createVNode(_unref(BTooltip), { target: "reference-organism-icon" }, {
                      default: _withCtx(() => [...(_cache[40] || (_cache[40] = [
                        _createTextVNode(" The reference organism value specifies the original organism from which the cells were obtained. It serves for alignment of the sequences to genome. ", -1),
                        _createElementVNode("strong", null, "Its correct specification is critical.", -1),
                        _createTextVNode(" Current options are `human` and `mouse`. By default, the value is set to `human`. ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _createVNode(_unref(BFormInput), {
                    modelValue: adminPipelineReferenceOrganism.value,
                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((adminPipelineReferenceOrganism).value = $event)),
                    id: "pipeline-reference-organism",
                    name: "pipeline-reference-organism",
                    placeholder: "Enter sample reference organism (e.g. mouse) or leave blank for default (human)"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createVNode(_unref(BFormGroup), { class: "mt-3" }, {
                default: _withCtx(() => [
                  _createElementVNode("label", null, [
                    _cache[43] || (_cache[43] = _createTextVNode(" Custom barcodes whitelist ", -1)),
                    _createVNode(_component_font_awesome_icon, {
                      id: "custom-barcode-icon",
                      icon: "question-circle",
                      class: "ms-2 text-secondary"
                    }),
                    _createVNode(_unref(BTooltip), { target: "custom-barcode-icon" }, {
                      default: _withCtx(() => [...(_cache[42] || (_cache[42] = [
                        _createTextVNode(" Specify the custom barcodes whitelist to override the default option. See sample details for the default barcodes whitelist. ", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _createVNode(_unref(BFormInput), {
                    modelValue: adminPipelineBarcodeWhitelist.value,
                    "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((adminPipelineBarcodeWhitelist).value = $event)),
                    id: "pipeline-barcode-whitelist",
                    name: "pipeline-barcode-whitelist",
                    placeholder: "Enter custom barcodes whitelist or leave blank for default"
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }),
              _createElementVNode("label", _hoisted_9, [
                _cache[45] || (_cache[45] = _createTextVNode(" UMI Length ", -1)),
                _createVNode(_component_font_awesome_icon, {
                  id: "custom-umi-length-icon",
                  icon: "question-circle",
                  class: "ms-2 text-secondary"
                }),
                _createVNode(_unref(BTooltip), { target: "custom-umi-length-icon" }, {
                  default: _withCtx(() => [...(_cache[44] || (_cache[44] = [
                    _createTextVNode(" Specify the custom UMI length to override the default option. See sample details for the default UMI length. ", -1)
                  ]))]),
                  _: 1
                })
              ]),
              _createVNode(_unref(BFormGroup), null, {
                default: _withCtx(() => [
                  _createVNode(_unref(BInputGroup), null, {
                    prepend: _withCtx(() => [
                      _createVNode(_unref(BButton), { variant: "outline-secondary" }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(BFormCheckbox), {
                            modelValue: adminPipelineUmiLengthOverride.value,
                            "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((adminPipelineUmiLengthOverride).value = $event)),
                            name: "pipeline-umi-length-override"
                          }, null, 8, ["modelValue"])
                        ]),
                        _: 1
                      })
                    ]),
                    default: _withCtx(() => [
                      _createVNode(_unref(BFormInput), {
                        modelValue: adminPipelineUmiLength.value,
                        "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((adminPipelineUmiLength).value = $event)),
                        modelModifiers: { number: true },
                        onChange: _cache[13] || (_cache[13] = ($event: any) => (adminPipelineUmiLengthOverride.value = true)),
                        type: "number",
                        id: "pipeline-umi-length",
                        name: "pipeline-umi-length"
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }, 8, ["modelValue"])
        ], 2),
        (_unref(userStore).developer)
          ? (_openBlock(), _createBlock(DeveloperOptions, {
              key: 0,
              ref_key: "devOptions",
              ref: devOptions,
              size: "sm",
              class: "mt-3",
              computeTaskType: _unref(COMPUTE_TASK_TYPES).NEXTFLOW_PIPELINE
            }, null, 8, ["computeTaskType"]))
          : _createCommentVNode("", true),
        _createVNode(_unref(BAlert), {
          modelValue: mutationError.value,
          "onUpdate:modelValue": _cache[15] || (_cache[15] = ($event: any) => ((mutationError).value = $event)),
          variant: "danger",
          dismissible: "",
          class: "mt-3",
          onDismissed: _cache[16] || (_cache[16] = ($event: any) => (mutationError.value = null))
        }, {
          default: _withCtx(() => [
            _createTextVNode(_toDisplayString(mutationError.value), 1)
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createVNode(CloudRegionSelection, {
          modelValue: customCloudRegionPrefix.value,
          "onUpdate:modelValue": _cache[17] || (_cache[17] = ($event: any) => ((customCloudRegionPrefix).value = $event)),
          modelRequired: customCloudRegionRequired.value,
          "onUpdate:modelRequired": _cache[18] || (_cache[18] = ($event: any) => ((customCloudRegionRequired).value = $event)),
          class: "mt-3"
        }, null, 8, ["modelValue", "modelRequired"])
      ]),
      _: 1
    }, 8, ["modelValue", "backdrop", "no-close-on-esc"])
  ]))
}
}

})